/* global MatgenGlobal */

import { v4 as UUID } from 'uuid';
import MatgenAPI from './matgen-api.js';
import {
  buildTenantSavePayload,
  normalizeTenantRecord,
} from '../util/tenant.js';
import {
  buildCognitoAgreementReportQuery,
  normalizeCognitoAgreementReportRecord,
} from '../util/cognito-agreement-report.js';

class MatgenData {
  constructor() {
    this.API = new MatgenAPI();
  }

  getTemplateFileURL(id, tenant_id, ext) {
    return `https://${MatgenGlobal.AmplifyConfig.Storage.bucket}.s3.amazonaws.com/tenant/${tenant_id}/templates/${id}${ext}`;
  }

  async getOptionPreviewURL(id) {
    const option = await this.API.request(`/options/${id}`);
    const component = await this.API.request(
      `/components/${option.component_id}`
    );
    let template = await this.API.request(
      `/templates/${component.template_id}`
    );
    if (Array.isArray(template) && template.length > 0) {
      template = template[0];
    }
    return `https://${MatgenGlobal.AmplifyConfig.Storage.bucket}.s3.amazonaws.com/tenant/${template.tenant_id}/options/${id}.png`;
  }

  async getMaterialPreviewURL(id, pid) {
    try {
      return await MatgenGlobal.Amplify.Storage.get(`${id}/${pid}.png`, {
        level: 'protected',
      });
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async sendEmail(data) {
    const response = await this.API.request(`/mailer`, 'POST', data);
    return response;
  }

  getTable(table, arg = null) {
    if (arg) table += arg;
    return this.API.request(`/${table}`);
  }

  async getUsers(s) {
    const response = await this.API.request(`/users?s=${s}`);
    return response;
  }

  async getCognitoAgreementReport({
    tenantId = false,
    acceptedFrom = false,
    acceptedTo = false,
  } = {}) {
    const query = buildCognitoAgreementReportQuery({
      tenantId,
      acceptedFrom,
      acceptedTo,
    });
    const response = await this.API.request(
      `/cognito_agreement_report${query ? `?${query}` : ''}`
    );
    return Array.isArray(response)
      ? response.map(record => normalizeCognitoAgreementReportRecord(record))
      : response;
  }

  async saveCognitoAgreementReport(record = {}) {
    return this.API.request('/cognito_agreement_report', 'POST', record);
  }

  async getCognitoAgreementDocument(id) {
    if (!id) {
      return false;
    }
    return this.API.request(
      `/cognito_agreement_report/${encodeURIComponent(id)}/document`
    );
  }

  async getTenantId(name) {
    const response = await this.API.request(`/tenants?name=${name}`);
    return response ? normalizeTenantRecord(response[0]) : false;
  }

  async getTenants() {
    const response = await this.API.request(`/tenants`);
    return Array.isArray(response)
      ? response.map(tenant => normalizeTenantRecord(tenant))
      : response;
  }

  async getTenant(id) {
    const response = await this.API.request(`/tenants/${id}`);
    if (Array.isArray(response)) {
      return response.length > 0
        ? normalizeTenantRecord(response[0])
        : response;
    }
    return normalizeTenantRecord(response);
  }

  async getQuestionnaires(tenant_id) {
    const response = await this.API.request(
      `/questionnaires?tenant_id=${tenant_id}`
    );
    return response;
  }

  async getQuestionnaire(id) {
    const response = await this.API.request(`/matgen-questionnaire/${id}`);
    return response;
  }

  async getStudyDataQuestions() {
    const response = await this.API.request(`/study_data_questions/`);
    return response;
  }

  async getQuestionnaireRecord(id) {
    const response = await this.API.request(`/questionnaire/${id}`);
    return response;
  }

  async getQuestionnaireSections(id) {
    const response = await this.API.request(`/sections?questionnaire_id=${id}`);
    return response;
  }

  async getSectionQuestions(id) {
    const response = await this.API.request(`/questions?section_id=${id}`);
    return response;
  }

  async getSection(id) {
    const response = await this.API.request(`/sections/${id}`);
    return response;
  }

  async getQuestion(id) {
    const response = await this.API.request(`/questions/${id}`);
    return response;
  }

  async getAnswer(id) {
    const response = await this.API.request(`/answers/${id}`);
    return response;
  }

  async getQuestionAnswers(id) {
    const response = await this.API.request(`/answers?question_id=${id}`);
    return response;
  }

  async getTemplate(id) {
    let response = await this.API.request(`/templates/${id}`);
    if (Array.isArray(response) && response.length > 0) {
      response = response[0];
    }
    return response;
  }

  async getComponents(id) {
    const response = await this.API.request(`/components?template_id=${id}`);
    return response;
  }

  async getMicrosite(id) {
    const response = await this.API.request(`/microsites/${id}`);
    return response;
  }

  async getMaterial(id) {
    const response = await this.API.request(`/materials/${id}`);
    return response;
  }

  async getTemplates(tenant_id) {
    if (tenant_id) {
      const response = await this.API.request(
        `/templates?tenant_id=${tenant_id}`
      );
      return response;
    } else {
      const response = await this.API.request(`/templates`);
      return response;
    }
  }

  async getTaggedTemplates(tenant_id, tags) {
    const response = await this.API.request(`/templates`, 'POST', {
      tenant_id,
      tags,
    });
    return response;
  }

  async getTemplateCheck(question_id) {
    const response = await this.API.request(
      `/template-check/${question_id}`,
      'GET'
    );
    return response;
  }

  async getTemplateTags(id) {
    const response = await this.API.request(`/tags?template_id=${id}`);
    return response;
  }

  async getMaterialPageFile(id, pageId) {
    try {
      const res = await this.API.getS3File({
        path: `${id}/${pageId}.json`,
        type: 'json',
        noCache: false,
        level: 'protected',
      });
      return res;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getTemplateFile(id) {
    let page, template, res;
    try {
      page = await MatgenGlobal.Data.getPage(id);
    } catch (e) {
      console.error(e);
      return false;
    }
    try {
      template = await MatgenGlobal.Data.getTemplate(page.template_id);
    } catch (e) {
      console.error(e);
      return false;
    }

    try {
      res = await this.API.getS3File({
        path: `tenant/${template.tenant_id}/templates/${id}.json`,
        type: 'json',
        noCache: MatgenGlobal.AuthUser.getUserRole() === 'super',
        customPrefix: { public: '' },
      });
      return res;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getPage(id) {
    try {
      const page = await this.API.request(`/pages/${id}`);
      return page;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getPages(id) {
    try {
      const pages = await this.API.request(`/pages?template_id=${id}`);
      return pages;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getFirstPage(id) {
    try {
      const pages = await this.API.request(`/pages?template_id=${id}`);
      if (!Array.isArray(pages) || pages.length === 0) {
        return false;
      }

      // Older templates can have string page numbers or start at a value
      // other than zero. The editor should still open the earliest page.
      return [
        [...pages].sort(
          (a, b) => Number(a.number || 0) - Number(b.number || 0)
        )[0],
      ];
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getComponent(id) {
    try {
      const response = await this.API.request(`/components/${id}`);
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getDefaultOption(id) {
    if (!id) {
      console.error('Undefined component ID');
      return false;
    }
    try {
      const component = await this.getComponent(id);

      if (!component) {
        console.error('Component not found for ID:', id);
        return false;
      }
      const template = await this.getTemplate(component.template_id);
      let json = false;
      let oid = component.default_option_id;
      try {
        json = await this.getComponentOption(oid, template.tenant_id);
      } catch (e) {
        console.error('Bad default option');
      }

      if (!json) {
        const opts = await this.getComponentOptions(id);
        if (opts && opts[0]) {
          oid = opts[0].id;
          json = await this.getComponentOption(oid, template.tenant_id);
          if (
            json !== false &&
            (MatgenGlobal.AuthUser.getUserRole() === 'super' ||
              MatgenGlobal.AuthUser.getUserRole() === 'admin')
          ) {
            const default_update_response = await this.saveComponent(
              {
                id,
                default_option_id: oid,
              },
              true
            );
            console.log(default_update_response);
          }
        }
      }
      if (!json) {
        console.error(
          'Failed getting default option JSON:',
          component.default_option_id,
          json
        );
        return false;
      }
      if (Array.isArray(json)) {
        json = json.map(async o => {
          //const obj = JSON.parse(o);
          o.currentOptionId = oid;
          return JSON.stringify(o);
        });
      } else {
        json.currentOptionId = oid;
      }
      return { json, id: oid };
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getComponentOption(id, tenant_id) {
    try {
      const res = await this.API.getS3File({
        path: `tenant/${tenant_id}/options/${id}.json`,
        type: 'json',
        noCache: MatgenGlobal.AuthUser.getUserRole() === 'super',
        customPrefix: { public: '' },
      });
      return res;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getComponentOptions(id) {
    try {
      const json = await this.API.request(`/options?component_id=${id}`);
      let results = json;
      if (MatgenGlobal.AuthUser.getUserRole() === 'user') {
        results = json.filter(o => o.enabled);
      }
      return results;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteTenant(id) {
    try {
      const response = await this.API.request(`/tenant/${id}`, 'DELETE');
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteAnswer(id) {
    try {
      const response = await this.API.request(`/answer/${id}`, 'DELETE');
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteQuestion(id) {
    try {
      const response = await this.API.request(`/question/${id}`, 'DELETE');
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteSection(id) {
    try {
      const response = await this.API.request(`/section/${id}`, 'DELETE');
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteQuestionnaire(id) {
    try {
      const response = await this.API.request(`/questionnaire/${id}`, 'DELETE');
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteComponent(id) {
    try {
      const response = await this.API.request(`/component/${id}`, 'DELETE');
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteTemplateFiles(tenant_id, page_id) {
    try {
      const response = await MatgenGlobal.Amplify.Storage.remove(
        `tenant/${tenant_id}/templates/${page_id}.png`,
        {
          level: 'public',
          customPrefix: { public: '' },
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      const response2 = await MatgenGlobal.Amplify.Storage.remove(
        `tenant/${tenant_id}/templates/${page_id}.json`,
        {
          level: 'public',
          customPrefix: { public: '' },
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      return { response, response2 };
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteTemplateAsset(tenant_id, filename) {
    if (!tenant_id || !filename) {
      return true;
    }
    try {
      return await MatgenGlobal.Amplify.Storage.remove(
        `tenant/${tenant_id}/templates/${filename}`,
        {
          level: 'public',
          customPrefix: { public: '' },
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteTemplateAssets(template, pages) {
    const filenames = new Set();
    const pagePreviewExtensions = new Set(['.png', template.preview_image_ext]);

    pages.forEach(page => {
      filenames.add(`${page.id}.json`);
      pagePreviewExtensions.forEach(extension => {
        if (extension) {
          filenames.add(`${page.id}${extension}`);
        }
      });
    });

    if (template.file_ext) {
      filenames.add(`${template.id}${template.file_ext}`);
    }
    if (template.preview_image_ext) {
      filenames.add(`${template.id}${template.preview_image_ext}`);
    }
    if (template.associated_file) {
      filenames.add(template.associated_file);
    }

    const deleted = await Promise.all(
      [...filenames].map(filename =>
        this.deleteTemplateAsset(template.tenant_id, filename)
      )
    );
    return deleted.every(result => result !== false);
  }

  async deletePage(page_id) {
    try {
      const api_response = await this.API.request(`/page/${page_id}`, 'DELETE');
      return api_response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteComponentOptions(tenant_id, component_id) {
    try {
      const options = await this.API.request(
        `/options?component_id=${component_id}`
      );
      const filesDeleted = await Promise.all(
        options.map(o => this.deleteOptionFile(o.id, tenant_id))
      );
      const previewsDeleted = await Promise.all(
        options.map(o => this.deleteOptionPreview(o.id, tenant_id))
      );
      if (
        filesDeleted.some(result => result === false) ||
        previewsDeleted.some(result => result === false)
      ) {
        return false;
      }
      await Promise.all(
        options.map(o => this.API.request(`/option/${o.id}`, 'DELETE'))
      );
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async duplicateTemplate(id, name) {
    MatgenGlobal.UI.loading('Retrieving template data...');

    try {
      const template = await this.API.request(`/templates/${id}`);
      if (template === false) {
        return false;
      }

      if (template.type === 'FILE') {
        MatgenGlobal.UI.loading('Copying template files...');

        const templateFile = await this.API.getS3File({
          path: `tenant/${template.tenant_id}/templates/${template.id}${template.file_ext}`,
          customPrefix: { public: '' },
        });

        const newTemplate = Object.assign({}, template);
        newTemplate.id = UUID();
        newTemplate.name = name;

        let res;
        res = await this.saveTemplate(newTemplate);
        console.log(res);
        if (templateFile) {
          res = await this.saveTemplateFile(
            `${newTemplate.id}${newTemplate.file_ext}`,
            newTemplate.tenant_id,
            templateFile
          );
          console.log(res);
        }

        if (
          newTemplate.preview_type === 'IMAGE' &&
          newTemplate.preview_image_ext
        ) {
          const templatePreview = await this.API.getS3File({
            path: `tenant/${template.tenant_id}/templates/${template.id}${template.preview_image_ext}`,
            customPrefix: { public: '' },
          });
          if (templatePreview) {
            res = await this.saveTemplateFile(
              `${newTemplate.id}${newTemplate.preview_image_ext}`,
              newTemplate.tenant_id,
              templatePreview
            );
            console.log(res);
          }
        }
        MatgenGlobal.UI.stopLoading();
      } else {
        MatgenGlobal.UI.loading('Copying template files...');

        const pageIdMap = {};
        const componentIdMap = {};
        const optionIdMap = {};

        const pages = await this.API.request(`/pages?template_id=${id}`);
        if (pages === false) {
          return false;
        }
        const components = await this.API.request(
          `/components?template_id=${id}`
        );

        let options = await Promise.all(
          components.map(c => this.getComponentOptions(c.id))
        );
        options = options.flat();

        const newTemplate = Object.assign({}, template);
        newTemplate.id = UUID();
        newTemplate.name = name;

        const newPages = pages.map(p => {
          const newPage = Object.assign({}, p);
          newPage.id = UUID();
          newPage.template_id = newTemplate.id;

          pageIdMap[newPage.id] = p.id;

          return newPage;
        });

        const newOptions = [];
        //const componentsCopy = merge({}, components);
        //const optionsCopy = merge({}, options);

        const newComponents = components.map(c => {
          const newComponent = Object.assign({}, c);
          newComponent.id = UUID();
          newComponent.template_id = newTemplate.id;

          componentIdMap[newComponent.id] = c.id;

          options
            .filter(o => o.component_id === c.id)
            .map(o => {
              const newOption = Object.assign({}, o);
              newOption.id = UUID();
              newOption.component_id = newComponent.id;
              if (o.id === c.default_option_id) {
                newComponent.default_option_id = newOption.id;
              }
              optionIdMap[newOption.id] = o.id;
              newOptions.push(newOption);
            });

          return newComponent;
        });

        const pageFiles = {};
        const pagePreviews = {};

        for (let i = 0; i < pages.length; i++) {
          const p = pages[i];
          const pageJSON = await this.API.getS3File({
            path: `tenant/${template.tenant_id}/templates/${p.id}.json`,
            type: 'json',
            customPrefix: { public: '' },
          });
          pageFiles[p.id] = pageJSON;
          const ext = template.preview_image_ext
            ? template.preview_image_ext
            : '.png';
          const pagePreview = await this.API.getS3File({
            path: `tenant/${template.tenant_id}/templates/${p.id}${ext}`,
            customPrefix: { public: '' },
          });
          pagePreviews[p.id] = pagePreview;
        }

        const optionFiles = {};
        const optionPreviews = {};

        for (let i = 0; i < options.length; i++) {
          const o = options[i];
          const optionJSON = await this.API.getS3File({
            path: `tenant/${template.tenant_id}/options/${o.id}.json`,
            type: 'json',
            customPrefix: { public: '' },
          });
          optionFiles[o.id] = optionJSON;
          const optionPreview = await this.API.getS3File({
            path: `tenant/${template.tenant_id}/options/${o.id}.png`,
            customPrefix: { public: '' },
          });
          optionPreviews[o.id] = optionPreview;
        }

        const tempPages = {};
        Object.keys(pageFiles).forEach(k => {
          tempPages[k] = JSON.stringify(pageFiles[k]);
        });

        const tempOptions = {};
        Object.keys(optionFiles).forEach(k => {
          tempOptions[k] = JSON.stringify(optionFiles[k]);
        });

        for (let j = 0; j < Object.keys(tempPages).length; j++) {
          const k = Object.keys(tempPages)[j];
          const regex = new RegExp(template.id, 'gm');
          tempPages[k] = tempPages[k].replace(regex, newTemplate.id);
        }

        for (let j = 0; j < Object.keys(tempOptions).length; j++) {
          const k = Object.keys(tempOptions)[j];
          const regex = new RegExp(template.id, 'gm');
          tempOptions[k] = tempOptions[k].replace(regex, newTemplate.id);
        }

        for (let i = 0; i < newComponents.length; i++) {
          for (let j = 0; j < Object.keys(tempPages).length; j++) {
            const k = Object.keys(tempPages)[j];
            const regex = new RegExp(componentIdMap[newComponents[i].id], 'gm');
            tempPages[k] = tempPages[k].replace(regex, newComponents[i].id);
          }

          for (let j = 0; j < Object.keys(tempOptions).length; j++) {
            const k = Object.keys(tempOptions)[j];
            const regex = new RegExp(componentIdMap[newComponents[i].id], 'gm');
            tempOptions[k] = tempOptions[k].replace(regex, newComponents[i].id);
          }
        }

        for (let i = 0; i < newOptions.length; i++) {
          for (let j = 0; j < Object.keys(tempPages).length; j++) {
            const k = Object.keys(tempPages)[j];
            const regex = new RegExp(optionIdMap[newOptions[i].id], 'gm');
            tempPages[k] = tempPages[k].replace(regex, newOptions[i].id);
          }

          for (let j = 0; j < Object.keys(tempOptions).length; j++) {
            const k = Object.keys(tempOptions)[j];
            const regex = new RegExp(optionIdMap[newOptions[i].id], 'gm');
            tempOptions[k] = tempOptions[k].replace(regex, newOptions[i].id);
          }
        }

        MatgenGlobal.UI.loading('Saving new template...');

        let res;

        res = await this.saveTemplate(newTemplate);
        console.log(res);

        for (let i = 0; i < newPages.length; i++) {
          const p = newPages[i];
          res = await this.savePage(p);
          console.log(res);
        }

        for (let i = 0; i < newComponents.length; i++) {
          const c = newComponents[i];
          res = await this.saveComponent(c);
          console.log(res);
        }

        for (let i = 0; i < newOptions.length; i++) {
          const o = newOptions[i];
          res = await this.saveOptionData(o);
          console.log(res);
        }

        await Promise.all(
          newPages.map(async p => {
            res = await this.saveTemplateFile(
              `${p.id}.json`,
              newTemplate.tenant_id,
              tempPages[pageIdMap[p.id]]
            );
            console.log(res);
            res = await this.saveTemplateFile(
              `${p.id}.png`,
              newTemplate.tenant_id,
              pagePreviews[pageIdMap[p.id]]
            );
            console.log(res);
          })
        );

        await Promise.all(
          newOptions.map(async o => {
            res = await this.saveTemplateFile(
              `${o.id}.json`,
              newTemplate.tenant_id,
              tempOptions[optionIdMap[o.id]],
              true
            );
            console.log(res);

            res = await this.saveTemplateFile(
              `${o.id}.png`,
              newTemplate.tenant_id,
              optionPreviews[optionIdMap[o.id]],
              true
            );
            console.log(res);
          })
        );
      }
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteTemplatePage(id) {
    MatgenGlobal.UI.loading('Deleting pages...');
    try {
      const page = await this.getPage(id);
      const template = await this.getTemplate(page.template_id);
      await this.deleteTemplateFiles(template.tenant_id, id);
      await this.deletePage(id);
    } catch (e) {
      console.error(e);
      MatgenGlobal.UI.handleError(
        'Error deleting page',
        'There was a problem deleting the page record and/or files.'
      );
      MatgenGlobal.UI.stopLoading();
      return false;
    }
    MatgenGlobal.UI.stopLoading();
  }

  async deleteTemplate(id) {
    MatgenGlobal.UI.loading('Retrieving template data...');
    try {
      const template = await this.getTemplate(id);
      if (template === false) {
        return false;
      }
      const pages = await this.API.request(`/pages?template_id=${id}`);
      const components = await this.API.request(
        `/components?template_id=${id}`
      );
      // Filter client-side rather than trusting an optional query parameter on
      // the materials endpoint. A backend that ignores unknown filters must
      // never cause us to remove unrelated material records.
      const materialRecords = await this.API.request(`/materials`);
      if (
        !Array.isArray(pages) ||
        !Array.isArray(components) ||
        !Array.isArray(materialRecords)
      ) {
        return false;
      }
      const materials = materialRecords.filter(
        material => material.template_id === id
      );

      if (materials.length > 0) {
        MatgenGlobal.UI.loading('Deleting dependent materials...');
        const materialsDeleted = await Promise.all(
          materials.map(material => this.deleteMaterial(material.id))
        );
        if (materialsDeleted.some(result => result === false)) {
          return false;
        }
      }

      MatgenGlobal.UI.loading('Deleting components and options...');
      const optionsDeleted = await Promise.all(
        components.map(c =>
          this.deleteComponentOptions(template.tenant_id, c.id)
        )
      );
      if (optionsDeleted.some(result => result === false)) {
        return false;
      }

      const assetsDeleted = await this.deleteTemplateAssets(template, pages);
      if (!assetsDeleted) {
        return false;
      }

      MatgenGlobal.UI.loading('Deleting pages and records...');
      const studyDataDeleted = await Promise.all(
        pages.map(page => this.deleteAllTemplateStudyData(id, page.id))
      );
      if (studyDataDeleted.some(result => result === false)) {
        return false;
      }

      const pagesDeleted = await Promise.all(
        pages.map(p => this.deletePage(p.id))
      );
      const componentsDeleted = await Promise.all(
        components.map(c => this.deleteComponent(c.id))
      );
      if (
        pagesDeleted.some(result => result === false) ||
        componentsDeleted.some(result => result === false)
      ) {
        return false;
      }

      const api_response = await this.API.request(`/templates/${id}`, 'DELETE');
      return api_response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteMaterial(id) {
    try {
      const materialData = await this.API.request(`/materials/${id}`);
      if (materialData === false) {
        return false;
      }
      const pages = await this.API.request(
        `/pages?template_id=${materialData.template_id}`
      );
      if (!Array.isArray(pages)) {
        return false;
      }
      const deletedFiles = await Promise.all(
        pages.flatMap(page => [
          this.deleteMaterialFile(id, `${page.id}.json`),
          this.deleteMaterialFile(id, `${page.id}.png`),
        ])
      );
      if (deletedFiles.some(result => result === false)) {
        return false;
      }
      const api_response = await this.API.request(`/materials/${id}`, 'DELETE');
      if (api_response === false) {
        return false;
      }
      return api_response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteMaterialFile(id, filename) {
    try {
      return await MatgenGlobal.Amplify.Storage.remove(`${id}/${filename}`, {
        level: 'protected',
        progressCallback(e) {
          MatgenGlobal.UI.loaderProgress(e);
        },
      });
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteOption(id, component_id, force = false) {
    let component, options;
    try {
      component = await this.getComponent(component_id);
      if (component === false) {
        MatgenGlobal.UI.handleError(
          'Server Error',
          'There was a problem locating the component for this option.'
        );
        return false;
      }
    } catch (e) {
      console.error(e);
      return false;
    }

    if (component.default_option_id === id) {
      try {
        options = await this.getComponentOptions(component_id);
      } catch (e) {
        console.error(e);
        return false;
      }
      options = options.filter(o => o.id !== id);
      if (options === false) {
        MatgenGlobal.UI.handleError(
          'Server Error',
          'There was a problem locating the other options to set new default.'
        );
        return false;
      }
      if (options.length === 0 && !force) {
        MatgenGlobal.UI.handleError(
          'Application Error',
          'You cannot delete this option, as it is the only option.'
        );
        return false;
      }
      let default_update_response;
      try {
        if (!force) {
          default_update_response = await this.saveComponent(
            {
              id: component_id,
              default_option_id: options[0].id,
            },
            true
          );
          console.log(default_update_response);
        }
      } catch (e) {
        console.error(e);
        return false;
      }
    }
    const response = await this.API.request(`/options/${id}`, 'DELETE');
    return response;
  }

  async deleteOptionFile(id, tenant_id = false) {
    if (!tenant_id) {
      console.error('Missing tenant id');
      return false;
    }
    try {
      const response = await MatgenGlobal.Amplify.Storage.remove(
        `tenant/${tenant_id}/options/${id}.json`,
        {
          level: 'public',
          customPrefix: { public: '' },
        }
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteOptionPreview(id, tenant_id = false) {
    if (!tenant_id) {
      console.error('Missing tenant id');
      return false;
    }
    try {
      const response = await MatgenGlobal.Amplify.Storage.remove(
        `tenant/${tenant_id}/options/${id}.png`,
        {
          level: 'public',
          customPrefix: { public: '' },
        }
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveTags({ tags, id } = {}) {
    try {
      const response = await this.API.request(`/tags/`, 'POST', { tags, id });
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveMaterial(material, update = false) {
    try {
      const response = await this.API.request(
        `/materials/`,
        update ? 'PATCH' : 'POST',
        material
      );

      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async archiveMaterial(id, archived = true) {
    return this.saveMaterial({ id, archived: archived ? 1 : 0 }, true);
  }

  async saveComponent(component, update = false) {
    try {
      const response = await this.API.request(
        `/components/`,
        update ? 'PATCH' : 'POST',
        component
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveOptionData(option, update = false) {
    try {
      const response = await this.API.request(
        `/options`,
        update ? 'PATCH' : 'POST',
        option
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveOptionFile(id, data, tenant_id = false) {
    if (!tenant_id) {
      console.error('Missing tenant id');
      return false;
    }
    try {
      const response = await MatgenGlobal.Amplify.Storage.put(
        `tenant/${tenant_id}/options/${id}.json`,
        data,
        {
          level: 'public',
          customPrefix: { public: '' },
          contentType: 'application/json',
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveOptionImage(base64Data, id, tenant_id = false) {
    if (!tenant_id) {
      console.error('Missing tenant id');
      return false;
    }
    try {
      const response = await MatgenGlobal.Amplify.Storage.put(
        `tenant/${tenant_id}/options/${id}.png`,
        base64Data,
        {
          level: 'public',
          customPrefix: { public: '' },
          contentType: 'image/png',
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveTemplate(template, update = false) {
    try {
      const response = await this.API.request(
        `/templates`,
        update ? 'PATCH' : 'POST',
        template
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async archiveTemplate(id, archived = true) {
    return this.saveTemplate({ id, archived: archived ? 1 : 0 }, true);
  }

  async savePage(page) {
    try {
      const response = await this.API.request(`/pages`, 'POST', page);
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveTemplateFile(filename, tenant_id, data, option = false) {
    const folder = option === false ? 'templates' : 'options';
    try {
      const response = await MatgenGlobal.Amplify.Storage.put(
        `tenant/${tenant_id}/${folder}/${filename}`,
        data,
        {
          level: 'public',
          customPrefix: { public: '' },
          contentType: data.type,
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async savePageFile(id, data, tenant_id = false) {
    if (!tenant_id) {
      console.error('Missing tenant id');
      return false;
    }
    try {
      /*console.log('data:');
      data.objects.forEach(d => {
        console.log(d);
      });*/

      const response = await MatgenGlobal.Amplify.Storage.put(
        `tenant/${tenant_id}/templates/${id}.json`,
        JSON.stringify(data),
        {
          level: 'public',
          customPrefix: { public: '' },
          contentType: 'application/json',
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveMaterialPageFile(id, pageId, data) {
    try {
      const response = await MatgenGlobal.Amplify.Storage.put(
        `${id}/${pageId}.json`,
        JSON.stringify(data),
        {
          level: 'protected',
          contentType: 'application/json',
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveTemplateImage(base64Data, tenant_id = false) {
    if (!tenant_id) {
      console.error('Missing tenant id');
      return false;
    }
    try {
      const response = await MatgenGlobal.Amplify.Storage.put(
        `tenant/${tenant_id}/templates/${MatgenGlobal.editor.curPageId}.png`,
        base64Data,
        {
          level: 'public',
          customPrefix: { public: '' },
          contentType: 'image/png',
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveMaterialImage(base64Data, id, pid = MatgenGlobal.editor.curPageId) {
    try {
      const response = await MatgenGlobal.Amplify.Storage.put(
        `${id}/${pid}.png`,
        base64Data,
        {
          level: 'protected',
          contentType: 'image/png',
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveItem(type, item, update = false) {
    try {
      const response = await this.API.request(
        `/${type}`,
        update ? 'PATCH' : 'POST',
        item
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveTenant(tenant, update = false) {
    try {
      const payload = buildTenantSavePayload({}, tenant);
      const response = await this.API.request(
        `/tenants`,
        update ? 'PATCH' : 'POST',
        payload
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveQuestionnaire(tenant, update = false) {
    try {
      const response = await this.API.request(
        `/questionnaires`,
        update ? 'PATCH' : 'POST',
        tenant
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveMicrosite(
    id,
    subdomain,
    user_name,
    study_id,
    template_id,
    html,
    json,
    edit = false,
    last_edit
  ) {
    try {
      if (edit === false) {
        const db_response = await this.API.request(`/microsites`, 'POST', {
          id,
          subdomain,
          user_name,
          study_id,
          template_id,
        });
        console.log(db_response);
      } else {
        const params = {
          id,
          subdomain,
          user_name,
          study_id,
          template_id,
          published: false,
          edit: true,
        };
        if (last_edit) {
          params.last_edit = last_edit;
        }
        const db_response = await this.API.request(
          `/microsites`,
          'POST',
          params
        );
        console.log(db_response);
      }
      /*const html_response = await MatgenGlobal.Amplify.Storage.put(
        `microsites/${subdomain}/index.html`,
        html,
        {
          bucket: MatgenGlobal.MICROSITE_BUCKET,
          level: 'public',
          customPrefix: { public: '' },
          contentType: 'text/html',
          cacheControl: 'no-cache',
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      console.log(html_response);*/
      /*const json_response = await MatgenGlobal.Amplify.Storage.put(
        `${subdomain}.json`,
        JSON.stringify(json),
        {
          level: 'protected',
          contentType: 'application/json',
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      console.log(json_response);*/
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deployMicrosite(subdomain, html) {
    try {
      const html_response = await MatgenGlobal.Amplify.Storage.put(
        `microsites/${subdomain}/index.html`,
        html,
        {
          bucket: MatgenGlobal.MICROSITE_BUCKET,
          level: 'public',
          customPrefix: { public: '' },
          contentType: 'text/html',
          cacheControl: 'no-cache',
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      console.log(html_response);
    } catch (e) {
      console.error(e);
      MatgenGlobal.UI.handleError(
        null,
        'There was a problem ddeploying the website. Please try again later or contact support.'
      );
    }
  }

  async checkMicrosites(user_name) {
    try {
      const response = await this.API.request(`/microsites-check/${user_name}`);
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteMicrosite(id) {
    try {
      const microsite = await this.API.request(`/microsites/${id}`);
      if (microsite === false) {
        return false;
      }
      const response = await this.API.request(`/microsites/${id}`, 'DELETE');
      console.log(response);
      const file_response = await MatgenGlobal.Amplify.Storage.remove(
        `microsites/${microsite.subdomain}/index.html`,
        {
          bucket: MatgenGlobal.MICROSITE_BUCKET,
          level: 'public',
          customPrefix: { public: '' },
          progressCallback(e) {
            MatgenGlobal.UI.loaderProgress(e);
          },
        }
      );
      console.log(file_response);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getStudyData(study_id = false) {
    let response;

    if (study_id) {
      response = await this.API.request(`/study_data?study_id=${study_id}`);
    } else {
      response = await this.API.request(`/study_data`);
    }
    return response;
  }

  async getTemplateStudyData(template_id = false) {
    let response;

    if (template_id) {
      response = await this.API.request(
        `/template_study_data?template_id=${template_id}`
      );
    } else {
      response = await this.API.request(`/template_study_data`);
    }
    return response;
  }

  async deleteStudyData(study_id, question_id) {
    try {
      const response = await this.API.request(
        `/study_data?study_id=${study_id}&question_id=${question_id}`,
        'DELETE'
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteAllTemplateStudyData(template_id, page_id) {
    try {
      const response = await this.API.request(
        `/template_study_data?template_id=${template_id}&page_id=${page_id}`,
        'DELETE'
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteTemplateStudyData(template_id, question_id) {
    try {
      const response = await this.API.request(
        `/template_study_data?template_id=${template_id}&question_id=${question_id}`,
        'DELETE'
      );
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveStudyData(study_data, update = false) {
    try {
      const response = await this.API.request(
        `/study_data/`,
        update ? 'PATCH' : 'POST',
        study_data
      );

      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async saveTemplateStudyData(studyData, update = false) {
    try {
      //console.log(studyData);
      const response = await this.API.request(
        `/template_study_data/`,
        update ? 'PATCH' : 'POST',
        studyData
      );
      return response;
    } catch (e) {
      if (
        e.response.data.error &&
        e.response.data.error.code === 'ER_DUP_ENTRY'
      ) {
        return false;
      }
      console.error(e);
      return false;
    }
  }
}

export { MatgenData };
